if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    static STORAGE_KEY = 'artmie_recently_viewed';
    static CARD_SECTION_ID = 'section-recently-viewed-card';

    constructor() {
      super();
      this.swiperInstance = null;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.currentProductHandle = this.dataset.currentProductHandle;
      this.productsLimit = parseInt(this.dataset.productsLimit || '12');
      
      // Track current product if on product page
      if (this.currentProductHandle) {
        this.trackProduct(this.currentProductHandle);
      }
      
      // ARTMIE_LAZY_CAROUSEL v2 2026-06-26: defer load+render+Swiper off critical load path; guaranteed setTimeout fallback (element is display:none until populated). trackProduct above stays immediate.
      var self = this;
      var go = function(){ if (self.__armieInit) return; self.__armieInit = true; if (self.__armieIO) self.__armieIO.disconnect(); self.loadRecentlyViewedProducts(); };
      if ('IntersectionObserver' in window) {
        self.__armieIO = new IntersectionObserver(function(en){ for (var i=0;i<en.length;i++){ if(en[i].isIntersecting){ go(); break; } } }, { rootMargin: '600px 0px' });
        self.__armieIO.observe(self);
      }
      setTimeout(go, 2500);
    }

    trackProduct(handle) {
      if (!handle) return;
      
      try {
        const stored = localStorage.getItem(RecentlyViewedProducts.STORAGE_KEY);
        let handles = stored ? JSON.parse(stored) : [];
        
        // Remove if already exists
        handles = handles.filter(h => h !== handle);
        
        // Add to beginning
        handles.unshift(handle);
        
        // Limit to 20 products
        if (handles.length > 20) {
          handles = handles.slice(0, 20);
        }
        
        localStorage.setItem(RecentlyViewedProducts.STORAGE_KEY, JSON.stringify(handles));
      } catch (e) {
        console.error('Failed to track product:', e);
      }
    }

    getStoredProductHandles() {
      try {
        const stored = localStorage.getItem(RecentlyViewedProducts.STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        return [];
      }
    }

    async loadRecentlyViewedProducts() {
      const storedHandles = this.getStoredProductHandles();

      if (storedHandles.length === 0) {
        this.style.display = 'none';
        return;
      }

      // Filter out current product
      const filteredHandles = this.currentProductHandle
        ? storedHandles.filter((handle) => handle !== this.currentProductHandle)
        : storedHandles;

      if (filteredHandles.length === 0) {
        this.style.display = 'none';
        return;
      }

      // Fetch up to 2x productsLimit handles so we still fill the carousel
      // after dropping any out-of-stock products (the card template returns
      // empty for unavailable products via `if product_ref.available`).
      const fetchLimit = Math.min(filteredHandles.length, this.productsLimit * 2);
      const handlesToLoad = filteredHandles.slice(0, fetchLimit);

      try {
        const cardPromises = handlesToLoad.map((handle) => this.fetchProductCard(handle));
        const cardsHtml = await Promise.all(cardPromises);
        const productCards = cardsHtml
          .filter((html) => html !== null && html.trim() !== '')
          .slice(0, this.productsLimit);

        if (productCards.length === 0) {
          this.style.display = 'none';
          return;
        }

        this.renderProducts(productCards);
        this.style.display = 'block';
        
        // Initialize Swiper after rendering
        setTimeout(() => this.initSwiper(), 100);
      } catch (error) {
        console.error('Failed to load recently viewed products:', error);
        this.style.display = 'none';
      }
    }

    async fetchProductCard(handle) {
      try {
        const url = `${window.Shopify.routes.root}products/${handle}?section_id=${RecentlyViewedProducts.CARD_SECTION_ID}`;
        const response = await fetch(url);

        if (!response.ok) {
          return null;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Look for the product card content
        const slide = doc.querySelector('.recently-viewed-section__slide');

        if (!slide) {
          return null;
        }

        return slide.innerHTML;
      } catch (error) {
        console.error(`Failed to fetch product card for ${handle}:`, error);
        return null;
      }
    }

    renderProducts(productCards) {
      const wrapper = this.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      wrapper.innerHTML = '';
      
      productCards.forEach(cardHtml => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = cardHtml;
        wrapper.appendChild(slide);
      });
    }

    initSwiper() {
      const slider = this.querySelector('.swiper');
      if (!slider || this.swiperInstance) return;

      // Check if there are any slides
      const slides = slider.querySelectorAll('.swiper-slide');
      if (slides.length === 0) return;

      // Wait for Swiper library to load
      if (typeof Swiper === 'undefined') {
        setTimeout(() => this.initSwiper(), 100);
        return;
      }

      const nextButton = this.querySelector('.swiper-button-next');
      const prevButton = this.querySelector('.swiper-button-prev');
      const pagination = this.querySelector('.swiper-pagination');

      // Get settings from data attributes
      const desktopColumns = parseFloat(this.dataset.desktopColumns || '4');
      const mobileColumns = parseFloat(this.dataset.mobileColumns || '1.2');
      const gap = parseInt(this.dataset.gap || '24');
      const gapMobile = parseInt(this.dataset.gapMobile || '16');

      this.swiperInstance = new Swiper(slider, {
        slidesPerView: mobileColumns,
        spaceBetween: gapMobile,
        loop: false,
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
        pagination: {
          el: pagination,
          clickable: true,
          type: 'bullets',
          dynamicBullets: false,
        },
        breakpoints: {
          640: { 
            slidesPerView: Math.min(desktopColumns, 2), 
            spaceBetween: gap 
          },
          768: { 
            slidesPerView: Math.min(desktopColumns, 3), 
            spaceBetween: gap 
          },
          1024: { 
            slidesPerView: desktopColumns, 
            spaceBetween: gap 
          }
        },
        on: {
          init: function() {
            console.log('Recently Viewed Products Swiper initialized');
          }
        }
      });
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
